import "./TableWrapperV2Component.scss";
import {PaginationComponent} from "../../pagination/paginationComponent";
import {IAPIResponseType} from "../../../models/api.model";
import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {CommonService} from "../../../services";
import {AXIOS_REQUEST_CANCELLED} from "../../../services/api.service";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import {ITableColumn} from "../../../models/table.model";
import TooltipComponent from "../../tooltip/tooltipComponent";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HeadingComponent from "../../heading/headingComponent";
import CheckboxComponent from "../../core/checkbox/CheckboxComponent";
import ButtonComponent from "../../core/button/ButtonComponent";
import DrawerComponent from "../../drawer/drawerComponent";
import TableContainer from '@mui/material/TableContainer';
import TableV2Component from "../table-v2/TableV2Component";
import {isEqual} from "lodash";

interface TableWrapperV2ComponentProps {
    moduleName?: string;
    columns: any;
    id?: string;
    url: string,
    method: "get" | "post" | string,
    isPaginated?: boolean,
    extraPayload?: any;
    isColumnFilterVisible?: boolean;
    size?: "small" | "medium";
    hideTableHeader?: boolean;
    isStickyHeader?: boolean;
    isHoverable?: boolean;
    className?: any;
    onDataLoad?: Function;
}

const TableWrapperV2Component = (props: TableWrapperV2ComponentProps) => {

    const {queryParamData} = useSelector((state: IRootReducerState) => state.stateManagement);
    const {
        id,
        url,
        columns = [],
        method,
        size,
        isHoverable,
        hideTableHeader,
        isStickyHeader,
        isColumnFilterVisible,
        extraPayload,
        className,
        moduleName,
        onDataLoad,
        ...otherProps
    } = props;
    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
    // const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
    const [data, setData] = useState<any[]>([]);
    const pageNumRef = useRef<number>(0);
    const totalResultsRef = useRef<number>(0);
    const pageSizeRef = useRef<number>(extraPayload?.limit || 10);
    const isPaginated = props.isPaginated !== undefined ? props.isPaginated : true;
    const APICallSubscription = useRef<any>(null);
    const [renderingColumns, setRenderingColumns] = useState<ITableColumn[]>([]);
    const [columnFilterVisible, setColumnFilterVisible] = useState<boolean>(false);
    const tableRef = useRef<null | HTMLDivElement>(null);
    const [selectedColumns, setSelectedColumns] = useState<ITableColumn[]>([]);

    const handleToggleColumnFilters = useCallback(() => {
        setSelectedColumns(renderingColumns);
        setColumnFilterVisible(true);
    }, [renderingColumns]);


    const handleColumnFilterChange = useCallback((isChecked: boolean, column: ITableColumn) => {
        if (isChecked) {
            setSelectedColumns((oldState) => {
                const updatedColumns: ITableColumn[] = [...renderingColumns, ...oldState, column];
                const finalColumns: ITableColumn[] = [];
                columns.forEach((item: any) => {
                    if (updatedColumns.find((col) => item.id === col.id)) {
                        finalColumns.push(item);
                    }
                });
                return finalColumns;
            });
        } else {
            setSelectedColumns((oldState) => {
                return oldState.filter((item) => item.id !== column.id);
            });
        }
    }, [columns, renderingColumns]);

    const handleApplyColumnFilterChange = useCallback((isChecked: boolean, column: ITableColumn) => {
        setRenderingColumns(selectedColumns);
        setColumnFilterVisible(false);
    }, [selectedColumns]);

    const getListData = useCallback(() => {
        const cancelTokenSource = CommonService.getCancelToken();
        const payload = {page: pageNumRef.current, limit: pageSizeRef.current, ...extraPayload};
        if (!isPaginated) {
            delete payload.page;
            delete payload.limit;
        }
        let apiCall;
        if (method === "post") {
            apiCall = CommonService._api.post;
        } else {
            apiCall = CommonService._api.get;
        }
        if (APICallSubscription && APICallSubscription.current) {
            APICallSubscription.current.cancel();
        }
        APICallSubscription.current = cancelTokenSource;
        setIsDataLoading(true);
        // setIsDataLoaded(false);
        let listData: any[] = [];
        apiCall(url, payload, {}, {cancelToken: cancelTokenSource.token}).then((response: IAPIResponseType<any>) => {
            if (response.data) {
                listData = response?.data?.docs || response?.data || [];
                if (!listData?.length) {
                    listData = [];
                }
                pageNumRef.current = response?.data?.page;
                pageSizeRef.current = response?.data?.limit;
                totalResultsRef.current = response?.data?.total;
            }
            setData(listData);
            if (onDataLoad) {
                onDataLoad(listData);
            }
            setIsDataLoading(false);
            // setIsDataLoaded(true);
        }).catch((error) => {
            if (error.reason !== AXIOS_REQUEST_CANCELLED) { // if previous request got cancelled do not close loading state
                setData(listData);
                setIsDataLoading(false);
                // setIsDataLoaded(false);
            }
        })
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPaginated, method, url, extraPayload]);

    useEffect(() => {
        if (queryParamData) {
            pageNumRef.current = queryParamData.page || 1;
            pageSizeRef.current = queryParamData.limit || 10;
        }
        getListData();
    }, [getListData, queryParamData]);

    useEffect(() => {
        if (isColumnFilterVisible) {
            setRenderingColumns(columns.filter((col: any) => col.isDefaultColumn));
            setSelectedColumns(columns.filter((col: any) => col.isDefaultColumn));
        } else {
            setRenderingColumns(columns);
        }
    }, [columns, isColumnFilterVisible]);

    const handlePageNumberChange = useCallback((event: unknown, newPage: number) => {
        pageNumRef.current = newPage + 1;
        CommonService.setQueryParam('page', (newPage + 1).toString());
        getListData();
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [getListData]);

    const handlePageSizeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        pageSizeRef.current = +event.target.value;
        pageNumRef.current = 1;
        CommonService.setQueryParam('page', '1');
        CommonService.setQueryParam('limit', pageSizeRef.current.toString());
        getListData();
    }, [getListData]);

    useEffect(() => {
        const sub = CommonService._communications.TableWrapperRefreshSubject.subscribe((data) => {
            if (data.moduleName === moduleName) {
                getListData();
            }
        });
        return () => {
            sub.unsubscribe();
        }
    }, [getListData, moduleName]);

    return (
        <>
            <div className="table-v2-wrapper">
                <TableContainer className={"t-table-container " + className} ref={tableRef}>
                    <TableV2Component
                        data={data}
                        columns={renderingColumns}
                        loading={isDataLoading}
                        size={size}
                        {...otherProps}
                    />
                    <div className="t-table-column-filter-holder">
                        <div className="t-table-column-filter-container">
                            {isColumnFilterVisible && (data && data?.length) > 0 &&
                                <TooltipComponent tooltip={"Column Preferences"} arrow={true}>
                                    <span className="t-table-column-filter-toggler" onClick={handleToggleColumnFilters}>
                                        <MoreVertIcon/>
                                    </span>
                                </TooltipComponent>}
                        </div>
                    </div>
                </TableContainer>
                {
                    isPaginated &&
                    // /isDataLoaded && (data && data?.length) > 0) &&
                    <PaginationComponent
                        paginationOptions={[10, 25, 100]}
                        totalResults={totalResultsRef.current}
                        limit={pageSizeRef.current}
                        page={pageNumRef.current > 0 ? pageNumRef.current - 1 : 0}
                        onPageChange={handlePageNumberChange}
                        onRowsPerPageChange={handlePageSizeChange}
                    />
                }
                <DrawerComponent open={columnFilterVisible}
                                 closeOnBackDropClick={true}
                                 closeOnEsc={true}
                                 className={"drawer-wrapper"}
                                 onClose={() => setColumnFilterVisible(false)}
                >
                    <div className={'column-preferences-component screen t-form-screen'}>
                        <div className="drawer-sub-header horizontal-vertical-center-align">
                            <HeadingComponent title={"Column Preferences"}/>
                            {/*<div className="deselect-content"*/}
                            {/*     onClick={()=>{handleClearColumnFilter()}}>*/}
                            {/*    Deselect All*/}
                            {/*</div>*/}
                        </div>
                        <div className="t-form">
                            <div className="no-form t-form-drawer">
                                <div className="t-form-controls-wrapper">
                                    {
                                        columns.map((column: any, index: any) => {
                                            if (column.hideInColumnPreferences) {
                                                return <span key={column.id + index}/>
                                            } else {
                                                return <div className="pdd-5"
                                                            key={column.id + index}>
                                                    <CheckboxComponent
                                                        name={column?.id}
                                                        label={column.label}
                                                        disabled={column.isDefaultColumn}
                                                        checked={!!selectedColumns.find((item) => item.id === column.id)}
                                                        onUpdate={(isChecked: boolean) => {
                                                            handleColumnFilterChange(isChecked, column);
                                                        }
                                                        }
                                                    />
                                                </div>
                                            }
                                        })
                                    }
                                </div>
                                <div className="drawer-actions form-action-section">
                                    <ButtonComponent variant={"contained"}
                                                     fullWidth={true}
                                                     color={'primary'}
                                                     handleClick={handleApplyColumnFilterChange}>
                                        Apply
                                    </ButtonComponent>
                                </div>
                            </div>
                        </div>
                    </div>
                </DrawerComponent>
            </div>
        </>
    );
};

export default React.memo(TableWrapperV2Component, (prevProps: any, nextProps: any) => {
    return isEqual(prevProps, nextProps);
});

// ****************************** USAGE ****************************** //

// <TableWrapperV2Component
//     columns={[
//         {
//             key: "name",
//             dataIndex: "name",
//             title: "Name"
//         },
//         {
//             key: "description",
//             dataIndex: "description",
//             title: "Description"
//         }
//     ]}
//     rowKey={(record: any) => {
//         return record._id;
//     }}
//     url={ENV.API_URL + "/category/637caaa4ef59e8a8cdc9f4b3/service"}
//     method={"get"}
//     isPaginated={true}
// />

// ****************************** USAGE ****************************** //
